import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { filterUndefined, trackByRef, UrlPagedTable } from 'src/app/lego-utils';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  finalize,
  map,
  skip,
  switchMap,
  tap,
} from 'rxjs/operators';
import { generateEcoErrorMessage } from 'src/app/components/main-content/design-lookup/material-bom/generate-eco-error-message';
import { BOMItem } from 'src/app/models';
import { TeamcenterService } from 'src/app/services';
import {
  BOMHeaderService,
  BOMItemService,
  BOM_ITEM_PAGINATOR,
  DesignService,
  PagedCollection,
} from 'src/app/state';
import { CreateEditMaterialBomDrawer } from './create-edit-material-bom/create-edit-material-bom.drawer';

@Component({
  selector: 'em-material-bom',
  styleUrls: ['./material-bom.component.less'],
  templateUrl: './material-bom.component.html',
  standalone: false,
})
export class MaterialBomComponent implements OnInit, OnDestroy {
  public readonly trackByRef = trackByRef;
  public readonly table: UrlPagedTable<BOMItem, CreateEditMaterialBomDrawer>;
  public readonly routeParam;
  public readonly uiState$ = this.bomItemService.uiById$;
  public errorMsg?: string;

  public readonly materialBOMHeader$ = this.bomHeaderService.bomHeader$;
  public readonly isLoading$ = combineLatest([
    this.designService.loading$,
    this.bomHeaderService.loading$,
    this.paginator.loading$,
  ]).pipe(
    map(
      ([isDesignLoading, isBomHeaderLoading, isBomItemLoading]) =>
        isDesignLoading || isBomHeaderLoading || isBomItemLoading
    )
  );

  public readonly isBomLocked$ = this.bomHeaderService.isBomLocked$;
  public readonly bomType$ = this.bomItemService.bomType$;
  public readonly ecoNumberControl = new FormControl<string | undefined>(undefined);
  public readonly isBomCreated$ = this.bomHeaderService.isBomCreated$;

  public confirmDeleteHandlers = new Map<string, () => void>();

  public deleteHandlersMap: Record<string, () => void> = {};

  public generateEcoErrorMessage = generateEcoErrorMessage;

  public bomItemIdsLoading = new Set<string>();
  public editQueryParamsMap: Record<string, Record<string, unknown>> = {};

  public bomItemLoadingMap: Record<string, boolean> = {};

  private readonly refreshBomHeader$ = new BehaviorSubject<void>(undefined);
  private readonly subscriptions = new Subscription();

  public constructor(
    private readonly designService: DesignService,
    private readonly bomHeaderService: BOMHeaderService,
    private readonly teamcenterService: TeamcenterService,
    private readonly bomItemService: BOMItemService,
    private readonly modalService: NzModalService,
    private readonly notification: NzNotificationService,
    @Inject(BOM_ITEM_PAGINATOR) private readonly paginator: PagedCollection<BOMItem>,
    route: ActivatedRoute,
    router: Router,
    drawerService: NzDrawerService
  ) {
    this.table = new UrlPagedTable<BOMItem, CreateEditMaterialBomDrawer>({
      drawer: {
        componentType: CreateEditMaterialBomDrawer,
        drawerService,
        getSingle: (id) => this.bomItemService.getSingle(id),
        name: 'Material BOM Item',
        notification,
      },
      getPage: ({ page, size, sort, filters }) =>
        this.bomItemService.getPage(page, size, sort ? [sort] : undefined, filters),
      paginator,
      prefix: 'mbom',
      route,
      router,
    });

    this.routeParam = this.table.routeParam;
  }

  public ngOnInit(): void {
    this.subscriptions.add(this.table.connect());

    this.subscriptions.add(
      this.bomItemService.error$.subscribe((errorMsg) => {
        this.errorMsg = errorMsg;
      })
    );

    this.subscriptions.add(
      this.table.page$
        .pipe(distinctUntilChanged((prev, curr) => prev.data === curr.data))
        .subscribe((page) => {
          this.editQueryParamsMap = page.data.reduce<Record<string, Record<string, unknown>>>(
            (acc, item) => {
              acc[item.id] = this.routeParam.edit(item.id);

              return acc;
            },
            {}
          );
        })
    );

    this.subscriptions.add(
      this.refreshBomHeader$.pipe(switchMap(() => this.bomHeaderService.loadByDesign())).subscribe()
    );

    this.subscriptions.add(
      combineLatest([this.table.page$, this.isBomLocked$.pipe(filterUndefined())])
        .pipe(
          map(([page, isBomLocked]) => {
            const mapping: Record<string, () => void> = {};

            for (const item of page.data) {
              mapping[item.id] = () => {
                this.deleteBomItem(item.id, isBomLocked);
              };
            }

            return mapping;
          })
        )
        .subscribe((dhm) => {
          this.deleteHandlersMap = dhm;
        })
    );

    this.subscriptions.add(
      this.designService
        .selectDesignId()
        .pipe(
          filterUndefined(() => {
            this.bomHeaderService.resetStore();
            this.bomItemService.resetStore();
          }),
          // Skip the first designId to avoid double fetching the first time.
          skip(1)
        )
        .subscribe(() => {
          this.refreshBomHeader$.next();
          // Refresh table when designId changes. This is necessary because BOMItemDataService gets
          // the current designId directly from akita. A better solution could be to somehow pass
          // the designId as a parameter instead, but one of the issues is that the designId is not
          // a property on the BOMItem model.
          this.table.refresh();
        })
    );
  }

  public lockBOM(): void {
    const designId = this.designService.getDesignId();

    if (!designId) {
      throw new Error('No designId found!');
    }

    const lock$ = this.designService.set40C(designId).pipe(
      switchMap(() => this.bomHeaderService.lockBOM(designId)),
      tap(() => {
        this.notification.success(
          '40C set and BOM Locked',
          '40C was set and the BOM was successfully locked'
        );

        this.refreshBomHeader$.next();
      }),
      catchError((error) => {
        const errorMessage =
          typeof error === 'object' && error !== undefined && 'message' in error
            ? String((error as { message: unknown }).message).split('§§§')[0]
            : 'Unknown error';

        this.notification.error('Error', errorMessage, { nzDuration: 0 });

        return [];
      })
    );

    this.subscriptions.add(lock$.subscribe());
  }

  public createMaterialBom(): void {
    const designId = this.designService.getDesignId();

    if (!designId) {
      throw new Error('No designId found!');
    }

    const create$ = this.bomHeaderService.createMaterialBom(designId).pipe(
      tap(() => {
        this.notification.success('Material BOM created', 'The BOM was successfully created');
        this.refreshBomHeader$.next();
        this.table.refresh();
      }),
      catchError((error) => {
        const errorMessage =
          typeof error === 'object' && error !== undefined && 'message' in error
            ? String((error as { message: unknown }).message).split('§§§')[0]
            : 'Unknown error';

        this.notification.error('Error', errorMessage, { nzDuration: 0 });

        return [];
      })
    );

    this.subscriptions.add(create$.subscribe());
  }

  public deleteBomItem(id: string, isBomLocked: boolean) {
    const rawEcoNumber = this.ecoNumberControl.value;

    const ecoNumber: string | undefined =
      typeof rawEcoNumber === 'string' ? rawEcoNumber : undefined;

    this.ecoNumberControl.reset();

    if (isBomLocked && !ecoNumber) {
      this.notification.error('The BOM is locked!', 'An ECO number must be provided!');

      return;
    }

    if (!isBomLocked && !!ecoNumber) {
      throw new Error('ECO number cannot be provided unless the BOM is locked!');
    }

    const designId = this.designService.getDesignId();

    if (!designId) {
      throw new Error(`Trying to delete BomItem ${id} but designId is missing!`);
    }

    this.setBomItemLoadingState(id, true);

    this.subscriptions.add(
      this.bomItemService
        .delete(id, { ecoNumber })
        .pipe(
          tap({
            error: (error: unknown) => {
              // Try to read error.message if possible, otherwise fallback
              const sapErrorMessage =
                error && typeof error === 'object' && 'message' in error
                  ? String((error as { message: unknown }).message)
                  : 'Unknown error';

              const errorMessage = generateEcoErrorMessage(sapErrorMessage, isBomLocked);
              this.notification.error('Error', errorMessage, { nzDuration: 0 });
            },
          }),
          switchMap(() =>
            this.teamcenterService.startSync(designId).pipe(
              tap({
                error: (error: unknown) => {
                  const sapErrorMessage = `BOM item was deleted, but syncing SAP and Teamcenter failed. Syncing happens automatically every 15 minutes. Error message: ${
                    error instanceof Error ? error.message.split('§§§')[0] : 'Unknown error'
                  }`;

                  this.notification.error('Error', sapErrorMessage, { nzDuration: 0 });
                },
              }),
              finalize(() => {
                // Refresh table when sync succeeds/fails
                this.table.refresh();
              })
            )
          ),
          finalize(() => {
            this.setBomItemLoadingState(id, false);
          })
        )
        .subscribe()
    );
  }

  public setBomItemLoadingState(id: string, isLoading: boolean): void {
    if (isLoading) {
      this.bomItemIdsLoading.add(id);
    } else {
      this.bomItemIdsLoading.delete(id);
    }

    // Force view update by assigning a new Set reference
    this.bomItemIdsLoading = new Set(this.bomItemIdsLoading);

    // Rebuild the loading map for use in the template
    const newMap: Record<string, boolean> = {};

    for (const itemId of this.bomItemIdsLoading) {
      newMap[itemId] = true;
    }

    this.bomItemLoadingMap = newMap;
  }

  public getEditQueryParams(itemId: string): Record<string, unknown> {
    return this.routeParam.edit(itemId);
  }

  public showError(message: string): void {
    this.modalService.error({ nzContent: message, nzTitle: 'An error occurred!' });
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
